import React, { useCallback, useMemo } from 'react'

import { connect } from 'react-redux'
import { DatePicker } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { faCalendar, faClinicMedical, faGraduationCap, faSchool, faStethoscope, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import moment from 'moment'

import IconLabel, { FontSizes } from '../../../Components/icons/IconLabel'
import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import SchoolSectionSelector from '../../../Components/shared/Selectors/SchoolSectionSelector'
import StudentsSelector from '../../../Components/shared/Selectors/Student/StudentsSelector'
import SectorSelector from '../../../Components/shared/Selectors/SectorSelector'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import useRequest from '../../../hooks/useRequest'
import { EUROPEAN_DATE } from '../../../utils/constants'
import { isSchoolUser } from '../../../utils/roles'
import { canUserEditProperty, InternshipProperties } from '../../../utils/entities/internship'
import { getAllSchoolsPublic, getSchoolSchoolSections } from '../../../utils/api/school'
import { getSchoolSectionSectionStudents } from '../../../utils/api/schoolSection'
import { getInstitutionSectors } from '../../../utils/api/institution'
import { getEntityOrNull, getIdOrNull, isOptimistic } from '../../../utils'
import { ColorsPalette } from '../../../config/colors'

const COLUMN_WIDTH = '350px'
const FIELD_WIDTH = '200px'
const FIELD_STYLE = { width: FIELD_WIDTH }
const FIELD_ERROR_STYLE = { ...FIELD_STYLE, border: `2px solid ${ColorsPalette.RED}`, borderRadius: '4px' }

const { START_DATE, END_DATE, SCHOOL, SECTION, INSTITUTION, SECTOR, STUDENT } = InternshipProperties

const mapStateToProps = state => ({ user: selectUser(state), t: selectTranslate(state) })

const InternshipForm = ({ errorFields, data, sectors: preloadedSectors, onChange, user, t }) => {
  const isSchoolSide = isSchoolUser(user)

  const compatibleSchools = useRequest({
    request: getAllSchoolsPublic,
    parameters: useMemo(() => ({ filters: { sections: [getIdOrNull(data.bareSection)] }, paginate: false }), [data?.bareSection]),
    enabled: !isSchoolSide && data?.school !== null && data.bareSection !== null && !isOptimistic(data.bareSection)
  })

  const schoolSections = useRequest({
    request: getSchoolSchoolSections,
    parameters: useMemo(() => ({ id: getIdOrNull(data.school) }), [data?.school]),
    select: useCallback(responseData => responseData[data.school.id]?.filter(schoolSection => schoolSection.school === getIdOrNull(data.school) && getIdOrNull(schoolSection.sectionToYear)) ?? [], [data?.school]),
    enabled: !isSchoolSide && data?.school
  })

  const students = useRequest({
    request: getSchoolSectionSectionStudents,
    parameters: useMemo(() => ({ id: getIdOrNull(data.section) }), [data?.section]),
    enabled: isSchoolSide && data?.section
  })

  const sectors = useRequest({
    request: getInstitutionSectors,
    parameters: useMemo(() => ({ id: getIdOrNull(data.institution) }), [data?.institution]),
    enabled: isSchoolSide && data?.institution
  })

  const handleChange = key => (value, option, entity) => onChange(({ [key]: getEntityOrNull(entity) ?? getEntityOrNull(value) }))

  const handleEntityChange = (key, toReset = null) => (value, option, entity) => {
    handleChange(key)(value, option, entity)

    if (toReset) {
      handleChange(toReset)(null)
    }
  }

  const handleSectionChange = (value, option, entity) => onChange(({
    section: getEntityOrNull(entity) ?? getEntityOrNull(value),
    internshipYear: getEntityOrNull(entity.sectionToYear)
  }))

  const handleDateChange = key => value => onChange(({ [key]: moment(value) }))

  const filteredSections = useMemo(() => (isOptimistic(data.bareSection) || data.bareSection === null) ? schoolSections.data : schoolSections.data.filter(scSec => scSec.sectionId === getIdOrNull(data.bareSection)), [schoolSections.data, data.bareSection])
  const areCompatible = schoolSections.loading || data.bareSection === null || isOptimistic(data.bareSection) || (data.bareSection !== null && data.school !== null && schoolSections.data.some(scSec => scSec.sectionId === getIdOrNull(data.bareSection)))
  const isDisabled = useCallback(property => !canUserEditProperty(user, data, property), [user, data])

  const getStyle = field => errorFields.includes(field) || ([SCHOOL, SECTION].includes(field) && !areCompatible) ? FIELD_ERROR_STYLE : FIELD_STYLE

  return (
    <div className='flex-column'>
      <div className='form-class' style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' }}>
        {/* Colonne de gauche */}
        <div style={{ width: COLUMN_WIDTH }}>
          <FormItem label={<IconLabel icon={faCalendar} text={t('common.start_date')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <DatePicker
              value={data.startDate}
              format={EUROPEAN_DATE}
              onChange={handleDateChange(START_DATE)}
              style={getStyle(START_DATE)}
              disabled={isDisabled(START_DATE)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faSchool} text={t('common.school')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <SchoolsSelector
              schools={compatibleSchools.enabled ? compatibleSchools.data : null}
              values={getIdOrNull(data.school)}
              onChange={handleEntityChange(SCHOOL, SECTION)}
              style={getStyle(SCHOOL)}
              disabled={isDisabled(SCHOOL)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faGraduationCap} text={t('common.section_and_year')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <SchoolSectionSelector
              disabled={isDisabled(SECTION) || filteredSections.length === 0 || schoolSections.loading}
              schoolSections={filteredSections}
              values={!schoolSections.loading ? getIdOrNull(data.section) : null}
              onChange={handleSectionChange}
              style={getStyle(SECTION)}
              loading={schoolSections.loading}
            />
            {!areCompatible && (
              <>
                <br />
                <div style={{ color: ColorsPalette.RED, lineHeight: 'normal' }}>{t('internship_form.school_and_section_incompatible')}</div>
              </>
            )}
          </FormItem>
          {isSchoolSide && (
            <FormItem label={<IconLabel icon={faUserGraduate} text={t('common.student')} fontSize={FontSizes.SMALL} padding='0px' />}>
              <StudentsSelector
                disabled={students.data.length === 0 || students.loading || isDisabled(STUDENT)}
                students={students.data}
                values={getIdOrNull(data.student)}
                onChange={handleChange(STUDENT)}
                style={getStyle(STUDENT)}
                loading={students.loading}
              />
            </FormItem>
          )}
        </div>

        {/* Barre verticale */}
        <div
          style={{
            width: '2px',
            background: 'linear-gradient(to bottom, transparent, #d9d9d9, transparent)',
            alignSelf: 'stretch',
            margin: '0 8px'
          }}
        />

        {/* Colonne de droite */}
        <div style={{ width: COLUMN_WIDTH }}>
          <FormItem label={<IconLabel icon={faCalendar} text={t('common.end_date')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <DatePicker
              value={data.endDate}
              format={EUROPEAN_DATE}
              onChange={handleDateChange(END_DATE)}
              style={getStyle(END_DATE)}
              disabled={isDisabled(END_DATE)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faClinicMedical} text={t('common.institution')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <InstitutionsSelector
              multiple='default'
              values={isSchoolSide ? getIdOrNull(data.institution) : getIdOrNull(user.institutions[0])}
              onChange={handleEntityChange(INSTITUTION, SECTOR)}
              style={getStyle(INSTITUTION)}
              disabled={!isSchoolSide && isDisabled(INSTITUTION)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faStethoscope} text={t('common.sector')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <SectorSelector
              disabled={isDisabled(SECTOR) || !data.institution}
              sectors={isSchoolSide ? sectors.data : preloadedSectors}
              values={getIdOrNull(data.sector)}
              onChange={handleChange(SECTOR)}
              style={getStyle(SECTOR)}
            />
          </FormItem>
        </div>
      </div>
    </div>
  )
}

export default connect(mapStateToProps)(InternshipForm)
